import { useState, useEffect, useRef } from 'react';
import StaffNavbar from '../components/StaffNavbar';
import '../styles/StaffPages.css';
import uploadIcon from '../assets/icons/upload.svg';
import xmarkIcon from '../assets/icons/xmark.svg';
import apiService from '../services/api';

// Content Section Component
const ContentSection = ({ title, items, hoveredContentId, setHoveredContentId, handleEdit, handleStatusChange, handleDeactivateClick }) => (
  <div style={{ marginTop: '2rem' }}>
    <h2 style={{ color: '#333', marginBottom: '1.5rem', fontSize: '1.25rem' }}>
      {title} ({items.length})
    </h2>
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '20px'
    }}>
      {items.map((item) => (
        <div
          key={item.id}
          style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #e9ecef',
            maxWidth: '300px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '300px'
          }}
          onMouseEnter={() => setHoveredContentId(item.id)}
          onMouseLeave={() => setHoveredContentId(null)}
        >
          {/* Thumbnail */}
          <div style={{ margin: '-20px -20px 12px -20px' }}>
            {(item.featured_image || (item.images && item.images[0])) ? (
              <img
                src={item.featured_image || item.images[0]?.image_path}
                alt={item.title}
                style={{
                  width: '100%',
                  height: '150px',
                  objectFit: 'cover',
                  borderRadius: '12px 12px 0 0'
                }}
              />
            ) : (
              <div style={{
                width: '100%',
                height: '150px',
                backgroundColor: '#e9ecef',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px 12px 0 0',
                color: '#6c757d',
                fontSize: '14px'
              }}>
                <div>No Image</div>
              </div>
            )}
          </div>

          {/* Status Indicator */}
          <ContentStatusIndicator
            contentItem={item}
            isHovered={hoveredContentId === item.id}
            onEdit={() => handleEdit(item)}
            onStatusChange={(newStatus) => handleStatusChange(item.id, newStatus)}
            onDeactivateClick={() => handleDeactivateClick(item.id, item.title)}
          />

          {/* Content Area - takes up available space */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Title */}
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 'bold', color: '#333' }}>
              {item.title}
            </h3>

            {/* Subtitle */}
            {item.subtitle && (
              <p style={{ margin: '0 0 12px 0', color: '#666', fontSize: '14px', fontWeight: '500' }}>
                {item.subtitle}
              </p>
            )}

            {/* Description */}
            <p style={{
              margin: '0 0 12px 0',
              color: '#666',
              fontSize: '14px',
              lineHeight: '1.4',
              whiteSpace: 'pre-line',
              overflow: 'auto',
              maxHeight: '150px',
              padding: '5px',
              flex: 1
            }}>
              {item.description}
            </p>
          </div>

          {/* Meta info - sticks to bottom */}
          <p style={{ margin: '8px 0 0 0', color: '#888', fontSize: '12px', borderTop: '1px solid #e9ecef', paddingTop: '8px' }}>
            {item.content_type} • {item.view_count} views
          </p>

        </div>
      ))}
    </div>
  </div>
);

const StaffContent = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    category: 'Interview',
    featured: false,
    thumbnailFile: null,
    videoFile: null,
    videoUrl: '',
    existingImages: [],
    existingVideos: []
  });
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [videosToDelete, setVideosToDelete] = useState([]);
  const [hoveredContentId, setHoveredContentId] = useState(null);
  const [deactivateModal, setDeactivateModal] = useState({ show: false, contentId: null, title: '' });

  const categories = ['Interview', 'Content', 'Announcement'];

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await apiService.getContent();
      const contentData = response.results || response || [];
      // Ensure contentData is always an array
      setContent(Array.isArray(contentData) ? contentData : []);
    } catch (error) {
      console.error('Error fetching content:', error);
      setContent([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };


  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    }
  };

  // Drag and drop handlers for thumbnail
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));

    if (imageFile) {
      setFormData(prev => ({
        ...prev,
        thumbnailFile: imageFile
      }));
    }
  };

  // Drag and drop handlers for video
  const handleVideoDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleVideoDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleVideoDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleVideoDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files);
    const videoFile = files.find(file => file.type.startsWith('video/'));

    if (videoFile) {
      setFormData(prev => ({
        ...prev,
        videoFile: videoFile
      }));
    }
  };

  const handleImageUploadClick = () => {
    document.querySelector('input[name="thumbnailFile"]').click();
  };

  const handleVideoUploadClick = () => {
    document.querySelector('input[name="videoFile"]').click();
  };

  const removeThumbnail = () => {
    setFormData(prev => ({
      ...prev,
      thumbnailFile: null
    }));
  };

  const removeVideo = () => {
    setFormData(prev => ({
      ...prev,
      videoFile: null
    }));
  };

  const removeExistingImage = (imageId) => {
    console.log('Marking image for deletion:', imageId);
    // Track this image for deletion
    setImagesToDelete(prev => {
      const newList = [...prev, imageId];
      console.log('Updated imagesToDelete:', newList);
      return newList;
    });
    // Remove from form display
    setFormData(prev => ({
      ...prev,
      existingImages: prev.existingImages.filter(img => img.id !== imageId)
    }));
  };

  const removeExistingVideo = (videoId) => {
    console.log('Marking video for deletion:', videoId);
    // Track this video for deletion
    setVideosToDelete(prev => {
      const newList = [...prev, videoId];
      console.log('Updated videosToDelete:', newList);
      return newList;
    });
    // Remove from form display
    setFormData(prev => ({
      ...prev,
      existingVideos: prev.existingVideos.filter(vid => vid.id !== videoId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (editingContent) {
        // Delete images and videos that were marked for deletion FIRST
        console.log('Images to delete:', imagesToDelete);
        for (const imageId of imagesToDelete) {
          try {
            console.log('Deleting image with ID:', imageId);
            await apiService.deleteContentImage(imageId);
            console.log('Successfully deleted image:', imageId);
          } catch (error) {
            console.error('Error deleting image:', imageId, error);
          }
        }

        console.log('Videos to delete:', videosToDelete);
        for (const videoId of videosToDelete) {
          try {
            console.log('Deleting video with ID:', videoId);
            await apiService.deleteContentVideo(videoId);
            console.log('Successfully deleted video:', videoId);
          } catch (error) {
            console.error('Error deleting video:', videoId, error);
          }
        }

        // Update existing content (this will also upload new files if present)
        await apiService.updateContent(editingContent.id, formData);
      } else {
        // Create new content
        await apiService.createContent(formData);
      }

      // Refresh the content list
      await fetchContent();
      resetForm();
    } catch (error) {
      console.error('Error saving content:', error);
      // You might want to show a toast/error message here
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      description: '',
      category: 'Interview',
      featured: false,
      thumbnailFile: null,
      videoFile: null,
      videoUrl: '',
      existingImages: [],
      existingVideos: []
    });
    setImagesToDelete([]);
    setVideosToDelete([]);
    setShowForm(false);
    setEditingContent(null);
  };

  const handleEdit = (contentItem) => {
    setEditingContent(contentItem);
    setFormData({
      title: contentItem.title || '',
      subtitle: contentItem.subtitle || '',
      description: contentItem.description || '',
      category: apiService.mapContentTypeToCategory(contentItem.content_type) || 'Interview',
      featured: contentItem.featured || false,
      thumbnailFile: null,
      videoFile: null,
      videoUrl: contentItem.video_url || '',
      existingImages: contentItem.images || [],
      existingVideos: contentItem.videos || []
    });
    setShowForm(true);
  };


  const handleStatusChange = async (contentId, newStatus) => {
    try {
      await apiService.updateContentStatus(contentId, newStatus);
      await fetchContent();
    } catch (error) {
      console.error('Error updating content status:', error);
    }
  };

  const handleDeactivateClick = (contentId, title) => {
    setDeactivateModal({ show: true, contentId, title });
  };

  const handleConfirmDeactivate = async () => {
    try {
      await apiService.updateContentStatus(deactivateModal.contentId, 'deactivated');
      await fetchContent();
      setDeactivateModal({ show: false, contentId: null, title: '' });
    } catch (error) {
      console.error('Error deactivating content:', error);
    }
  };

  const handleCancelDeactivate = () => {
    setDeactivateModal({ show: false, contentId: null, title: '' });
  };

  // Group content by featured status, with drafts at the front of each list
  const featuredContent = [
    ...content.filter(c => c.featured === true && c.status === 'draft'),
    ...content.filter(c => c.featured === true && c.status === 'active')
  ];
  const generalContent = [
    ...content.filter(c => !c.featured && c.status === 'draft'),
    ...content.filter(c => !c.featured && c.status === 'active')
  ];
  const deactivatedContent = content.filter(c => c.status === 'deactivated');

  return (
    <>
    <div className="admin-dashboard">
      <StaffNavbar activeSection="content" />
      <div className="admin-main">
        <div className="admin-content">
          <div className="staff-page-header-row">
            <div>
              <h1 className="staff-page-header">Content Management</h1>
              <p className="staff-page-subheader">Manage exclusive content for your fans</p>
            </div>
            <button
              className="staff-page-button"
              onClick={() => {
                if (showForm) {
                  // Cancel/close form
                  setShowForm(false);
                  setEditingContent(null);
                  resetForm();
                } else {
                  setShowForm(true);
                }
              }}
            >
              <span className="staff-page-button-text">
                {showForm ? 'Cancel' : '+ Add Content'}
              </span>
            </button>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>Loading content...</p>
            </div>
          ) : (
            <>
          {showForm && (
            <div className="add-reward-section">
              <h1 className="staff-page-header">{editingContent ? 'Edit Content' : 'Add New Content'}</h1>
              <p className="staff-page-subheader">Fill out the content details</p>

              <form onSubmit={handleSubmit} className="reward-form">
                <div className="form-group">
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter content title"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Subtitle</label>
                  <input
                    type="text"
                    name="subtitle"
                    value={formData.subtitle}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter optional subtitle or tagline"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="form-textarea"
                    placeholder="Enter content description..."
                    rows="4"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="form-select"
                    required
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <div className="upload-area">
                    <div className="upload-title">Upload Thumbnail</div>
                    <div className="upload-subtext">Drag and drop image or click to browse</div>
                    {/* Show upload area if no existing images AND no new thumbnail file */}
                    {!formData.existingImages?.length && !formData.thumbnailFile && (
                      <div
                        className="upload-center"
                        onDragOver={handleDragOver}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <div className="upload-icon">
                          <img src={uploadIcon} alt="Upload" />
                        </div>
                        <div className="upload-text">Drop your image here</div>
                        <div className="upload-browse-text">or click to browse your files</div>
                        <div className="upload-content">
                          <input
                            type="file"
                            name="thumbnailFile"
                            onChange={handleFileChange}
                            accept="image/*"
                            style={{display: 'none'}}
                          />
                          <button type="button" className="staff-page-button upload-button" onClick={handleImageUploadClick}>
                            <span className="staff-page-button-text">Choose Image</span>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Show existing image */}
                    {formData.existingImages && formData.existingImages.length > 0 && (
                      <div className="image-preview-container" style={{ paddingTop: "50px" }}>
                        <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                          Existing Image:
                        </div>
                        <div className="image-preview-item">
                          <div className="preview-image-wrapper">
                            <img
                              src={formData.existingImages[0].image_path}
                              alt={formData.existingImages[0].alt_text || 'Uploaded image'}
                              style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '2px solid #ddd'
                              }}
                            />
                            <button
                              type="button"
                              className="remove-image-btn"
                              onClick={() => removeExistingImage(formData.existingImages[0].id)}
                              style={{
                                position: 'absolute',
                                top: '-8px',
                                right: '-8px',
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                backgroundColor: '#dc3545',
                                border: '2px solid white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: '12px',
                                color: 'white'
                              }}
                            >
                              ×
                            </button>
                          </div>
                          <div className="preview-name">
                            {formData.existingImages[0].caption || 'Image'}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show new thumbnail upload preview */}
                    {formData.thumbnailFile && (
                      <div className="image-preview-container" style={{ paddingTop: "20px" }}>
                        <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                          New Image to Upload:
                        </div>
                        <div className="image-preview-item">
                          <div className="preview-image-wrapper">
                            <img
                              src={URL.createObjectURL(formData.thumbnailFile)}
                              alt={formData.thumbnailFile.name}
                              style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '2px solid #ddd'
                              }}
                            />
                            <button
                              type="button"
                              className="remove-image-btn"
                              onClick={removeThumbnail}
                            >
                              <img src={xmarkIcon} alt="Remove" />
                            </button>
                          </div>
                          <div className="preview-name">{formData.thumbnailFile.name}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <div className="upload-area">
                    <div className="upload-title">Upload video</div>
                    <div className="upload-subtext">Drag and drop video or click to browse</div>
                    {/* Only show upload area if no existing video and no new video */}
                    {!formData.existingVideos?.length && !formData.videoFile && (
                      <div
                        className="upload-center"
                        onDragOver={handleVideoDragOver}
                        onDragEnter={handleVideoDragEnter}
                        onDragLeave={handleVideoDragLeave}
                        onDrop={handleVideoDrop}
                      >
                        <div className="upload-icon">
                          <img src={uploadIcon} alt="Upload" />
                        </div>
                        <div className="upload-text">Drop your video here</div>
                        <div className="upload-browse-text">or click to browse your files</div>
                        <div className="upload-content">
                          <input
                            type="file"
                            name="videoFile"
                            onChange={handleFileChange}
                            accept="video/*"
                            style={{display: 'none'}}
                          />
                          <button type="button" className="staff-page-button upload-button" onClick={handleVideoUploadClick}>
                            <span className="staff-page-button-text">Choose Video</span>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Show existing video */}
                    {formData.existingVideos && formData.existingVideos.length > 0 && (
                      <div className="image-preview-container"  style={{ paddingTop: "50px" }}>
                        <div style={{ marginBottom: '10px', paddingTop: "10px", fontSize: '14px', color: '#666' }}>
                          Existing Video:
                        </div>
                        <div className="image-preview-item">
                          <div className="preview-image-wrapper">
                            <div style={{
                              width: '80px',
                              height: '80px',
                              backgroundColor: '#e8f4f8',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '2px solid #007bff',
                              borderRadius: '8px',
                              fontSize: '24px'
                            }}>
                              🎬
                            </div>
                            <button
                              type="button"
                              className="remove-image-btn"
                              onClick={() => removeExistingVideo(formData.existingVideos[0].id)}
                              style={{
                                position: 'absolute',
                                top: '-8px',
                                right: '-8px',
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                backgroundColor: '#dc3545',
                                border: '2px solid white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: '12px',
                                color: 'white'
                              }}
                            >
                              ×
                            </button>
                          </div>
                          <div className="preview-name">
                            {formData.existingVideos[0].title || 'Video'}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show new video upload preview */}
                    {formData.videoFile && (
                      <div className="image-preview-container" style={{ paddingTop: "50px" }}>
                        <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                          New Video to Upload:
                        </div>
                        <div className="image-preview-item">
                          <div className="preview-image-wrapper">
                            <div style={{
                              width: '80px',
                              height: '80px',
                              backgroundColor: '#e8f4f8',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '2px solid #007bff',
                              borderRadius: '8px',
                              fontSize: '24px'
                            }}>
                              🎬
                            </div>
                            <button
                              type="button"
                              className="remove-image-btn"
                              onClick={removeVideo}
                            >
                              <img src={xmarkIcon} alt="Remove" />
                            </button>
                          </div>
                          <div className="preview-name">{formData.videoFile.name}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Or Video URL</label>
                  <input
                    type="url"
                    name="videoUrl"
                    value={formData.videoUrl}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="s3://app.gethomecrowd.com/assets/videos/..."
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleInputChange}
                    />
                    Featured Content
                  </label>
                  <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    Featured content appears at the top of the mobile app
                  </small>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="staff-page-button submit-button" disabled={isSubmitting}>
                    <span className="staff-page-button-text">
                      {isSubmitting ? (editingContent ? 'Updating...' : 'Creating...') : (editingContent ? 'Update' : 'Add')} Content
                    </span>
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Render content organized by sections */}
          {/* Featured Content Section */}
          {featuredContent.length > 0 && (
            <ContentSection
              title="Featured"
              items={featuredContent}
              hoveredContentId={hoveredContentId}
              setHoveredContentId={setHoveredContentId}
              handleEdit={handleEdit}
              handleStatusChange={handleStatusChange}
              handleDeactivateClick={handleDeactivateClick}
            />
          )}

          {/* General Content Section */}
          {generalContent.length > 0 && (
            <ContentSection
              title="General"
              items={generalContent}
              hoveredContentId={hoveredContentId}
              setHoveredContentId={setHoveredContentId}
              handleEdit={handleEdit}
              handleStatusChange={handleStatusChange}
              handleDeactivateClick={handleDeactivateClick}
            />
          )}

          {/* Deactivated Content Section */}
          {deactivatedContent.length > 0 && (
            <ContentSection
              title="Deactivated"
              items={deactivatedContent}
              hoveredContentId={hoveredContentId}
              setHoveredContentId={setHoveredContentId}
              handleEdit={handleEdit}
              handleStatusChange={handleStatusChange}
              handleDeactivateClick={handleDeactivateClick}
            />
          )}
            </>
          )}
        </div>
      </div>
    </div>

      {/* Deactivate Confirmation Modal */}
      {deactivateModal.show && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={handleCancelDeactivate}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#333' }}>
                Deactivate Content
              </h3>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <p style={{ margin: '0 0 8px 0', color: '#666' }}>
                Are you sure you want to deactivate "{deactivateModal.title}"?
              </p>
              <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                This will hide the content from users. You can reactivate it later.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                className="cancel-button"
                onClick={handleCancelDeactivate}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeactivate}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

// ContentStatusIndicator Component
const ContentStatusIndicator = ({ contentItem, isHovered, onEdit, onStatusChange, onDeactivateClick }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDotsClick = (e) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    setShowDropdown(false);
    onEdit();
  };


  const handleStatusChange = (newStatus) => (e) => {
    e.stopPropagation();
    setShowDropdown(false);
    onStatusChange(newStatus);
  };

  const getStatusInfo = () => {
    switch (contentItem.status) {
      case 'active':
        return { text: 'Active', color: '#22c55e' };
      case 'deactivated':
        return { text: 'Inactive', color: '#ef4444' };
      case 'draft':
      default:
        return { text: 'Draft', color: '#f59e0b' };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div ref={dropdownRef} style={{
      position: 'absolute',
      top: '8px',
      right: '8px',
      zIndex: 10
    }}>
      <div
        style={{
          width: isHovered ? '32px' : '70px',
          height: '32px',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.2s ease',
          cursor: isHovered ? 'pointer' : 'default'
        }}
        onClick={isHovered ? handleDotsClick : undefined}
      >
        {isHovered ? (
          // 3-dots menu
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            alignItems: 'center'
          }}>
            <div style={{
              width: '3px',
              height: '3px',
              backgroundColor: '#6b7280',
              borderRadius: '50%'
            }} />
            <div style={{
              width: '3px',
              height: '3px',
              backgroundColor: '#6b7280',
              borderRadius: '50%'
            }} />
            <div style={{
              width: '3px',
              height: '3px',
              backgroundColor: '#6b7280',
              borderRadius: '50%'
            }} />
          </div>
        ) : (
          // Show status text
          <span style={{
            fontSize: '10px',
            fontWeight: 'bold',
            color: statusInfo.color,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {statusInfo.text}
          </span>
        )}
      </div>

      {/* Dropdown Menu */}
      {showDropdown && (
        <div style={{
          position: 'absolute',
          top: '40px',
          right: '0',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          zIndex: 1000,
          minWidth: '120px',
          overflow: 'hidden'
        }}>
          <button
            onClick={handleEdit}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: 'none',
              backgroundColor: 'white',
              textAlign: 'left',
              fontSize: '14px',
              fontFamily: 'inherit',
              color: '#374151',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
          >
            Edit
          </button>

          {/* Status Change Options */}
          {contentItem.status === 'draft' && (
            <button
              onClick={handleStatusChange('active')}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: 'none',
                backgroundColor: 'white',
                textAlign: 'left',
                fontSize: '14px',
                fontFamily: 'inherit',
                color: '#22c55e',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
            >
              Activate
            </button>
          )}

          {contentItem.status === 'active' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDropdown(false);
                onDeactivateClick();
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: 'none',
                backgroundColor: 'white',
                textAlign: 'left',
                fontSize: '14px',
                fontFamily: 'inherit',
                color: '#ef4444',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
            >
              Deactivate
            </button>
          )}

          {contentItem.status === 'deactivated' && (
            <button
              onClick={handleStatusChange('active')}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: 'none',
                backgroundColor: 'white',
                textAlign: 'left',
                fontSize: '14px',
                fontFamily: 'inherit',
                color: '#22c55e',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
            >
              Reactivate
            </button>
          )}

        </div>
      )}
    </div>
  );
};

export default StaffContent;